import {AnimatePresence, motion, Variants} from 'framer-motion';
import {
  Attributes,
  Button,
  ButtonGroup,
  Card,
  DataStatus,
  Label,
  openDialog,
  Separator,
  showNotification,
} from 'platform/components';
import {Box, Center, Grid, HStack, Image, Space, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {indexBy, isEmpty, isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {EmployeeAuthorizationIntegrationBody} from '@dms/api/customer';
import i18n from '@dms/i18n';

import {parseDate, useToggle} from 'shared/web';

import {EMPTY_PLACEHOLDER} from '../../../constants/placeholders';
import {useBranches} from '../../../hooks/useBranches';
import mercedesLogoImage from '../assets/mercedes-benz-logo.png';
import safLogoImage from '../assets/sag-logo.png';
import tecRMIImage from '../assets/tecrmi-logo.png';
import {INTEGRATION_CODES} from '../constants/IntegrationCodes';
import {useListCredentials} from '../hooks/useListCredentials';
import {EditIntegrationForm} from './EditIntegrationForm';

interface IntegrationBoxProps {
  integrations: EmployeeAuthorizationIntegrationBody[];
  integrationCode: INTEGRATION_CODES;
  userId: string | undefined;
  isLoading: boolean;
}

/**
 * Integration box component with collapsible section functionality
 * @param props - Component props
 * @param props.integrations - Array of integration data
 * @returns JSX.Element
 */
export function IntegrationBox(props: IntegrationBoxProps) {
  const [isExpanded, toggleExpanded] = useToggle(false);

  const formatDateTime = useDateTimeFormatter();

  const buttonTitle = isExpanded
    ? i18n.t('page.rentalOrder.labels.showLess')
    : `${i18n.t('page.rentalOrder.labels.showMore')} (${props.integrations.length})`;

  const buttonIcon = isExpanded ? 'navigation/expand_less' : 'navigation/expand_more';

  const {data: branches, isLoading: isLoadingBranches} = useBranches();
  const branchesById = indexBy((branch) => branch.id, branches?.branchListItems ?? []);

  const {
    credentials,
    isLoading: isLoadingExternalCredentials,
    refetch: refetchExternalCredentials,
  } = useListCredentials({userId: props.userId ?? '', integrations: props.integrations});

  const handleEditIntegration = (
    id: string,
    title: string,
    credentialValues: Record<string, unknown>
  ) => {
    if (isNil(props.userId)) {
      showNotification.error();
      throw new Error('User ID is required');
    }

    openDialog(
      <EditIntegrationForm
        authorizationProfileId={id}
        integrationCode={props.integrationCode}
        userId={props.userId}
        defaultValues={credentialValues}
        onAfterSubmit={refetchExternalCredentials}
      />,
      {title}
    );
  };

  const isLoading = isLoadingBranches || isLoadingExternalCredentials;
  const image = match(props.integrationCode)
    .with(INTEGRATION_CODES.MERCEDES_XENTRY, () => mercedesLogoImage)
    .with(INTEGRATION_CODES.MERCEDES_CLARA, () => mercedesLogoImage)
    .with(INTEGRATION_CODES.MERCEDES_DAIMLER_XENTRY, () => mercedesLogoImage)
    .with(INTEGRATION_CODES.OTHER_SAG, () => safLogoImage)
    .with(INTEGRATION_CODES.OTHER_TECRMI, () => tecRMIImage)
    .exhaustive();

  return (
    <Box boxShadow="elevation_1" maxWidth={275}>
      <HStack>
        <Box width={40} minWidth={40} backgroundColor="palettes.neutral.20.100">
          <Center height={35}>
            <Image fit="contain" src={image} width={30} height={30} />
          </Center>
        </Box>

        <Box padding={4} backgroundColor="palettes.white.10.100" flexGrow={1}>
          <VStack>
            <VStack>
              <Label>{i18n.t('general.labels.name')}</Label>
              <Text alternative>
                {i18n.t(`page.employeeDetail.labels.${props.integrationCode}`)}
              </Text>
            </VStack>

            <Separator />

            <DataStatus isLoading={props.isLoading} isEmpty={isEmpty(props.integrations)}>
              <ButtonGroup>
                <Button
                  variant="link"
                  size="small"
                  leftIcon={buttonIcon}
                  title={buttonTitle}
                  onClick={toggleExpanded}
                />
              </ButtonGroup>

              <Space vertical={4} />

              <AnimatePresence initial={false}>
                {isExpanded && (
                  <motion.div
                    initial="collapsed"
                    animate="expanded"
                    exit="collapsed"
                    variants={variants}
                    transition={{duration: 0.3, ease: [0.25, 0.1, 0.25, 1]}}
                    style={{width: '100%'}}
                  >
                    <VStack spacing={2} width="100%">
                      {props.integrations.map((integration) => {
                        const integrationBranch = branchesById[integration.branchId];
                        const credential = credentials?.credentials?.find(
                          (credential) =>
                            credential.authorizationProfileId ===
                              integration.authorizationProfileId &&
                            credential.integratedServiceCode === props.integrationCode
                        );

                        const credentialValues = credential?.values ?? [];

                        const title = isNil(credential)
                          ? i18n.t('page.employeeDetail.labels.setup')
                          : i18n.t('general.labels.edit');

                        return (
                          <Card
                            key={integration.authorizationProfileId}
                            variant="inlineGrey"
                            title={integration.authorizationProfileName}
                            flags={
                              isLoading
                                ? []
                                : [
                                    {
                                      label: credential?.enabled
                                        ? i18n.t('general.labels.active')
                                        : i18n.t('entity.employee.labels.flagInactive'),
                                      colorScheme: credential?.enabled ? 'green' : 'red',
                                    },
                                  ]
                            }
                            actions={[
                              {
                                type: 'button',
                                isDisabled: isLoading,
                                variant: 'outlined',
                                title,
                                leftIcon: 'image/edit',
                                onClick: () =>
                                  handleEditIntegration(
                                    integration.authorizationProfileId,
                                    title,
                                    credentialValues.reduce(
                                      (acc, value) => {
                                        acc[value.name] = value.value;
                                        return acc;
                                      },
                                      {} as Record<string, unknown>
                                    )
                                  ),
                              },
                            ]}
                          >
                            <DataStatus isLoading={isLoading} minHeight={30}>
                              <VStack spacing={4}>
                                <Grid columns={4} align="center">
                                  <VStack>
                                    <Label>{i18n.t('entity.branch.labels.branch')}</Label>
                                    <Text alternative>
                                      {integrationBranch?.marketingName || EMPTY_PLACEHOLDER}
                                    </Text>
                                  </VStack>

                                  <VStack>
                                    <Label>{i18n.t('general.labels.createdAt')}</Label>
                                    <Text alternative>
                                      {credential?.createdAt
                                        ? formatDateTime(
                                            'dateShort',
                                            parseDate(credential.createdAt)
                                          )
                                        : EMPTY_PLACEHOLDER}
                                    </Text>
                                  </VStack>

                                  <VStack>
                                    <Label>{i18n.t('general.labels.updatedAt')}</Label>
                                    <Text alternative>
                                      {credential?.updatedAt
                                        ? formatDateTime(
                                            'dateShort',
                                            parseDate(credential.updatedAt)
                                          )
                                        : EMPTY_PLACEHOLDER}
                                    </Text>
                                  </VStack>
                                </Grid>

                                <DataStatus
                                  isEmpty={isNilOrEmpty(credential?.values)}
                                  spacing={4}
                                  emptyMessage={i18n.t(
                                    'page.employeeDetail.labels.integrationNotSetup'
                                  )}
                                >
                                  <Separator spacing={0} />
                                  <Attributes
                                    size="third"
                                    rows={credential?.values?.map((item) => ({
                                      label: item.name,
                                      value: item.value,
                                    }))}
                                  />
                                </DataStatus>
                              </VStack>
                            </DataStatus>
                          </Card>
                        );
                      })}
                    </VStack>
                  </motion.div>
                )}
              </AnimatePresence>
            </DataStatus>
          </VStack>
        </Box>
      </HStack>
    </Box>
  );
}

const variants: Variants = {
  collapsed: {
    height: 0,
    opacity: 0,
    overflow: 'hidden',
  },
  expanded: {
    height: 'auto',
    opacity: 1,
    overflow: 'visible',
  },
};
